import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useClientConfig } from '../api/clientConfig';
import { useAuth } from '../auth/AuthContext';

/**
 * Inactivity prompt, then automatic logout.
 *
 * After IDLE_WARNING_MINUTES of no user input this asks "are you still there?";
 * if nobody clicks within IDLE_GRACE_SECONDS it logs out. Both numbers come from
 * GET /api/config/client rather than being compiled in, because Create React App
 * inlines REACT_APP_* at image-build time -- a container env var in Azure would
 * silently do nothing.
 *
 * Two things here are deliberate and easy to "fix" wrongly:
 *
 *  - There is NO background poll of the server. Every authenticated request
 *    slides the server's own idle window (auth_service.get_identity touches
 *    last_seen_at), so a keepalive ping would defeat SESSION_IDLE_MINUTES, which
 *    is the backstop for the case this component cannot cover: a closed tab.
 *
 *  - Activity does NOT dismiss the prompt once it is up; only a click does. A
 *    stray mouse movement should not silently cancel a logout the user never
 *    saw, and "click to stay signed in" is the whole point of asking.
 *
 *  - The grace period is counted from when the prompt was SHOWN, not from the
 *    last activity. Those differ whenever the timer itself was suspended -- a
 *    slept laptop, or a background tab Chrome throttles to ~1 tick/minute --
 *    and counting from last activity meant the first tick after waking was
 *    already past warning+grace, so the user was logged out having never seen
 *    the prompt at all. That was the "logout inconsistent" report (issue #24).
 */

// Shared across tabs so working in one does not let another log you both out.
const ACTIVITY_KEY = 'policies:lastActivity';

// Anything a person does to the page. Kept to real input: an interval or a
// network response must not count as the user being present.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

// Cap how often activity is written. mousemove alone fires hundreds of times a
// second, and each write would otherwise hit localStorage and wake every tab.
const ACTIVITY_THROTTLE_MS = 1000;

function IdleTimeout() {
  const { authenticated, logout, refresh } = useAuth();
  // Shared, cached fetch -- the file pickers want the same config, and it never
  // changes within a session. Falls back to the backend defaults if unreachable;
  // a failed config fetch must not mean "no idle timeout at all".
  const config = useClientConfig();
  const [prompted, setPrompted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  // Refs, not state: these change on every mouse move and re-rendering the whole
  // subtree that often would be absurd. Only `prompted`/`secondsLeft` are state.
  const lastActivityRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const promptedRef = useRef(false);
  const promptShownAtRef = useRef(null);
  const loggingOutRef = useRef(false);
  const confirmButtonRef = useRef(null);

  const warningMs = config.idle_warning_minutes * 60 * 1000;
  const graceMs = config.idle_grace_seconds * 1000;

  const markActive = useCallback((timestamp) => {
    lastActivityRef.current = timestamp;
  }, []);

  // Activity tracking, mirrored to localStorage so sibling tabs agree on when
  // the *person* was last active rather than when this tab was.
  useEffect(() => {
    if (!authenticated) return undefined;

    const handleActivity = () => {
      // Once the prompt is up, ordinary input must not reset the clock -- only a
      // click on it may. Without this guard a stray mouse movement dismisses a
      // logout warning the user never read, which is the opposite of asking.
      // Note this also stops the prompt writing to localStorage, so during a
      // prompt the ONLY thing that can advance the shared timestamp is someone
      // clicking "I'm still here" in some tab -- which is exactly what should
      // dismiss it everywhere.
      if (promptedRef.current) return;

      const now = Date.now();
      markActive(now);
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      try {
        window.localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch (error) {
        // Private browsing / quota. Degrades to per-tab tracking, which is still
        // correct for a single tab -- not worth failing over.
      }
    };

    const handleStorage = (event) => {
      if (event.key !== ACTIVITY_KEY || !event.newValue) return;
      const other = Number(event.newValue);
      // Only ever move activity forward. A tab whose clock is behind must not be
      // able to drag another tab's idle timer backwards.
      if (Number.isFinite(other) && other > lastActivityRef.current) {
        markActive(other);
      }
    };

    // capture: true because `scroll` does not bubble -- without it, scrolling
    // any inner scrollable pane (a long document body, the comments panel)
    // never reaches `document` and does not count as the user being present.
    // The same options object must be passed to removeEventListener, or the
    // listener is not the one being removed and leaks.
    const listenerOptions = { passive: true, capture: true };

    ACTIVITY_EVENTS.forEach((name) => {
      document.addEventListener(name, handleActivity, listenerOptions);
    });
    window.addEventListener('storage', handleStorage);

    return () => {
      ACTIVITY_EVENTS.forEach(
        (name) => document.removeEventListener(name, handleActivity, listenerOptions));
      window.removeEventListener('storage', handleStorage);
    };
  }, [authenticated, markActive]);

  // Reset the clock whenever a session begins, so logging in does not inherit
  // however long the login screen sat untouched.
  useEffect(() => {
    if (!authenticated) {
      promptedRef.current = false;
      promptShownAtRef.current = null;
      loggingOutRef.current = false;
      setPrompted(false);
      setSecondsLeft(null);
      return;
    }
    // Adopt the shared timestamp if a sibling tab left a recent one. The
    // `storage` event only fires in OTHER tabs, so a tab that has just mounted
    // (reload, or a second tab opened later) would otherwise start its clock
    // from now and disagree with everyone else about when the person was last
    // active. Only a value that is in the past and still inside the current
    // window is adopted -- a stale one from a previous session must not prompt
    // somebody the instant they sign in.
    const now = Date.now();
    let stored = null;
    try {
      stored = Number(window.localStorage.getItem(ACTIVITY_KEY));
    } catch (error) {
      /* see handleActivity */
    }
    const usable = Number.isFinite(stored) && stored > 0
      && stored <= now && now - stored < warningMs + graceMs;
    markActive(usable ? stored : now);
  }, [authenticated, markActive, warningMs, graceMs]);

  // The single timer. One interval for both phases: the prompt threshold and the
  // grace countdown are just two distances from the same last-activity instant,
  // so a second timer would only be another thing to keep in step.
  useEffect(() => {
    if (!authenticated) return undefined;

    const tick = async () => {
      const now = Date.now();
      const idleFor = now - lastActivityRef.current;

      if (promptedRef.current) {
        // Activity moved forward while the prompt was up. Ordinary input cannot
        // do that (handleActivity refuses to while prompted), so this is only
        // ever "I'm still here" clicked in a SIBLING tab, arriving via the
        // storage event. Dismiss here too.
        if (idleFor < warningMs) {
          promptedRef.current = false;
          promptShownAtRef.current = null;
          setPrompted(false);
          setSecondsLeft(null);
          return;
        }

        // Grace counted from when the dialog was painted, not from last
        // activity -- see the note at the top of this file.
        const shownFor = now - (promptShownAtRef.current ?? now);
        if (shownFor >= graceMs) {
          if (loggingOutRef.current) return;
          loggingOutRef.current = true;
          try {
            // No navigate(): clearing auth state makes RequireAuth render
            // <Navigate to="/login" state={{ from }} />, which also preserves
            // where to return to after signing back in.
            await logout('inactivity');
          } catch (error) {
            // Must not reject out of an interval callback. Reset the guard so a
            // later tick can retry rather than leaving this wedged forever.
            console.error('Idle logout failed:', error);
            loggingOutRef.current = false;
          }
          return;
        }
        setSecondsLeft(Math.max(0, Math.ceil((graceMs - shownFor) / 1000)));
        return;
      }

      // Raise the prompt. This branch comes BEFORE any logout check: whatever
      // delayed the tick (a slept machine, a throttled hidden tab), the user
      // gets the dialog and a full countdown rather than being signed out
      // having never seen it.
      if (idleFor >= warningMs) {
        promptedRef.current = true;
        promptShownAtRef.current = now;
        setPrompted(true);
        setSecondsLeft(Math.ceil(graceMs / 1000));
      }
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [authenticated, warningMs, graceMs, logout]);

  useEffect(() => {
    if (prompted && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [prompted]);

  const handleStaySignedIn = async () => {
    const now = Date.now();
    markActive(now);
    try {
      window.localStorage.setItem(ACTIVITY_KEY, String(now));
    } catch (error) {
      /* see handleActivity */
    }
    promptedRef.current = false;
    promptShownAtRef.current = null;
    setPrompted(false);
    setSecondsLeft(null);
    // Re-reads /api/auth/me, which touches the session's last_seen_at server-side.
    // That keeps the server's idle window in step with the click rather than
    // leaving it counting from whenever the last real request happened.
    //
    // 'unreachable' is deliberately NOT a logout: this call used to time out
    // against a cold API and sign the user out through the very button they
    // pressed to stay signed in. If the session really is gone, refresh()
    // reports 'unauthenticated' and has already cleared state.
    const result = await refresh();
    if (result?.status === 'unreachable') {
      console.warn('Could not reach the API to extend the session; staying signed in.');
    }
  };

  if (!authenticated || !prompted) return null;

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-timeout-title"
        style={{ maxWidth: '440px' }}
      >
        <div className="modal-header">
          <h3 id="idle-timeout-title">👋 Are you still there?</h3>
        </div>

        <p style={{ color: 'var(--muted)' }}>
          You have been inactive for {config.idle_warning_minutes}{' '}
          minute{config.idle_warning_minutes === 1 ? '' : 's'}. To keep your
          work secure, we will sign you out in{' '}
          <strong aria-live="polite">{secondsLeft ?? config.idle_grace_seconds}</strong>{' '}
          second{secondsLeft === 1 ? '' : 's'}.
        </p>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => logout('user')}>
            Sign out now
          </button>
          <button
            type="button"
            className="btn btn-primary"
            ref={confirmButtonRef}
            onClick={handleStaySignedIn}
          >
            I'm still here
          </button>
        </div>
      </div>
    </div>
  );
}

export default IdleTimeout;
